import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox, IOption, MultiSelect } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { useGetApplicationRoles } from "@users/applicationuser/services/ApplicationUserService";

import "./applicationAssociation.css";

interface Props {
  applicationId: string;
  applicationName: string;
  appEnv?: string;
  isDisabled?: boolean;
  onAppRoleChange: (
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean,
    isChecked: boolean,
    errorMessage: string | undefined
  ) => void;
}

// Used for initial InviteUser flow from drawer on Organizations \ Users page
export function ApplicationAssociation(props: Readonly<Props>) {
  const {
    applicationId,
    applicationName,
    appEnv,
    isDisabled = false,
    onAppRoleChange
  } = props;

  // every application is being entered here 4 times
  // on parent component click causes two times render this component

  const { t } = useTranslation(undefined, { keyPrefix: "user.applications" });
  const { t: tApplicationRoles } = useTranslation(undefined, {
    keyPrefix: "application.roles"
  });

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<IOption[] | null>(
    null
  );
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const { applicationRoles, limit, isLoading, error } =
    useGetApplicationRoles(applicationId);

  useEffect(() => {
    // TBD refactor
    const roles: IOption[] = [];
    if (applicationRoles.length > 0) {
      applicationRoles.forEach((role: string) => {
        roles.push({ label: role, value: role });
      });
    }
    setOptions(roles);
  }, [applicationRoles]);

  const handleAppRoleChange = (options: IOption[] | null) => {
    setSelectedOptions(options);
    if (options === null) {
      const isValid = true;
      setIsValid(isValid);

      onAppRoleChange(
        applicationId,
        applicationName,
        [],
        isValid,
        isChecked,
        errorMessage
      );
      return [];
    }

    if (limit > -1 && options.length > limit) {
      const isValid = false;
      setIsValid(isValid);
      const errorMessage = t("role.validation.max-roles", { limit: limit });
      setErrorMessage(errorMessage);
      onAppRoleChange(
        applicationId,
        applicationName,
        options.map(item => item.value) as string[],
        isValid,
        isChecked,
        errorMessage
      );
      return [];
    }

    setIsValid(true);
    setErrorMessage(undefined);
    onAppRoleChange(
      applicationId,
      applicationName,
      options.map(item => item.value) as string[],
      true,
      isChecked,
      undefined
    );
    return options.map(item => item.value) as string[];
  };

  const handleCheckChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checkedValue = event.target.checked;

    setSelectedOptions(null);
    setDisabled(!checkedValue);
    setIsChecked(checkedValue);

    // assuming that roles are now NOT mandatory, by default we pass `true`
    // And no matter if it's checkbox is checked and represents Applications with Roles or no roles or UnChecked
    // It should be by default should be always form valid and be able to Save/Send
    const isValid = true;
    setIsValid(isValid);

    onAppRoleChange(
      applicationId,
      applicationName,
      [],
      isValid,
      checkedValue,
      errorMessage
    );
  };

  if (isLoading)
    return <LoadingComponent message={tApplicationRoles("load.message")} />;
  if (error)
    return <ErrorComponent message={tApplicationRoles("load.error.message")} />;

  const disabledCheckbox =
    (limit !== 0 && applicationRoles.length === 0) || isDisabled;

  let labelValue = applicationName;

  if (appEnv) {
    labelValue += ` (${appEnv})`;
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`application-association-${applicationId}`}
    >
      <div className="self-left">
        <Checkbox
          id={applicationId}
          name={`invite-application-${applicationId}`}
          data-testid={`invite-user-checkbox-${applicationId}`}
          label={labelValue}
          checked={isChecked}
          variant="alternative"
          disabled={disabledCheckbox}
          onChange={handleCheckChange}
        />
      </div>
      {limit !== 0 && applicationRoles.length === 0 && (
        <span>{tApplicationRoles("load.none.message")}</span>
      )}
      {limit !== 0 && applicationRoles.length > 0 && (
        <div data-testid={`invite-associated-applications-${applicationId}`}>
          <MultiSelect
            placeholder={tApplicationRoles("placeholder")}
            disabled={disabled || isDisabled}
            error={!isValid && !disabled}
            helperText={isValid ? undefined : errorMessage}
            options={options}
            selected={selectedOptions}
            onChange={handleAppRoleChange}
          />
        </div>
      )}
    </div>
  );
}
